import { useEffect, useId, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { Permission } from '@ithelpdesk/shared';
import { brandName } from '../api/runtime-config';
import { useSession } from '../api/session';
import { sessionPermissionSet } from '../api/session-permissions';
import type { SystemHealthStatus } from '../api/system-health';
import { cx } from '../ui/cx';
import { LiveCount } from '../features/live/LiveCount';
import type { CountName } from '../features/live/liveCounts';
import { useEscapeKey, useFocusTrap, useLocalStorageState, useMediaQuery } from '../ui/hooks';
import { AdminGroupIcon, BrandMark, ChevronDownIcon, CloseIcon } from './icons';
import { isNavItemVisible, NAV_ITEMS, type NavCounts, type NavFlags, type NavItem } from './nav';
import styles from './Sidebar.module.css';

/** Matches the layout breakpoint documented on the shell: below this, the sidebar becomes an off-canvas drawer instead of a persistent column. */
export const SIDEBAR_MOBILE_QUERY = '(max-width: 1023px)';

/** Stable id so the top bar's collapse button can point `aria-controls` at the column. */
export const SIDEBAR_ID = 'app-sidebar';

export interface SidebarHealth {
  status: SystemHealthStatus;
  isLoading: boolean;
  /** `service` = only API/database readiness was checked (non-admin tier). */
  scope?: 'full' | 'service';
}

export interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
  /** Real counts keyed by route; omitted keys render no badge. Passed in (not fetched here) so the sidebar stays a pure view. */
  counts?: NavCounts;
  /** Server-derived facts that reveal extra entries (e.g. Design Workflow participation). Passed in so the sidebar stays a pure view. */
  navFlags?: NavFlags;
  /** Desktop icon-rail mode. Owned by AppShell (the toggle lives in the top bar). Ignored in the mobile drawer. */
  collapsed?: boolean;
  /** Real readiness summary from `useSystemHealth()` (decision D-11). Omitted = "Checking status…", never a claim. */
  health?: SidebarHealth;
}

/** Nav badges whose number is a live server counter (animated + chimed on a genuine increase). */
const LIVE_COUNT_BY_ROUTE: Partial<Record<string, CountName>> = { '/unassigned': 'unassigned', '/my-tickets': 'assignedToMe' };

function formatCount(n: number): string {
  return n > 99 ? '99+' : String(n);
}

const HEALTH_COPY: Record<SystemHealthStatus, string> = {
  operational: 'All systems operational',
  degraded: 'Some services degraded',
  down: 'Service disruption',
  unknown: 'Checking status…',
};

function healthText(health: SidebarHealth | undefined): { tone: SystemHealthStatus; text: string } {
  if (!health || health.isLoading) return { tone: 'unknown', text: HEALTH_COPY.unknown };
  // A readiness-only check can't vouch for email/queues — don't say "all systems" (D-11).
  if (health.scope === 'service' && health.status === 'operational') return { tone: 'operational', text: 'Service operational' };
  return { tone: health.status, text: HEALTH_COPY[health.status] };
}

/**
 * Desktop: a persistent column, collapsible to an icon rail (toggle in the
 * top bar). The Admin group's open state is a per-viewer convenience
 * (`useLocalStorageState`, wrapped in try/catch), never anything the server
 * needs to know.
 *
 * Mobile (<1024px): an off-canvas drawer — focus-trapped, closes on Escape, a
 * backdrop click or its close button, and restores focus to whatever opened
 * it, exactly like `Modal` (same underlying hooks).
 *
 * Visibility here is UX only — see nav.ts. Every route re-checks server-side.
 */
export function Sidebar({ mobileOpen, onMobileClose, counts = {}, navFlags, collapsed = false, health }: SidebarProps) {
  const { data: session } = useSession();
  const [adminOpenPref, setAdminOpen] = useLocalStorageState('ithelpdesk.sidebar.adminOpen', true);
  const isMobile = useMediaQuery(SIDEBAR_MOBILE_QUERY);
  const drawerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const adminGroupId = useId();

  const actorPermissions: Set<Permission> = sessionPermissionSet(session);
  const visibleItems = NAV_ITEMS.filter((item) => isNavItemVisible(item, actorPermissions, navFlags));
  const mainItems = visibleItems.filter((i) => i.group !== 'admin');
  const adminItems = visibleItems.filter((i) => i.group === 'admin');
  const drawerActive = isMobile && mobileOpen;
  const onAdminRoute = location.pathname.startsWith('/admin');
  const adminOpen = adminOpenPref;
  // Navigating into the admin area opens the group so the active page is never
  // hidden. Keyed on the path only, so the user can still collapse it by hand
  // while staying on an admin page.
  useEffect(() => {
    if (onAdminRoute) setAdminOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useFocusTrap(drawerRef, drawerActive);
  useEscapeKey(onMobileClose, drawerActive);

  const isRail = !isMobile && collapsed;
  const showLabels = !isRail;
  const status = healthText(health);

  function renderItem(item: NavItem, nested = false) {
    const Icon = item.icon;
    const count = counts[item.to];
    const hasCount = typeof count === 'number' && count > 0;
    const liveName = LIVE_COUNT_BY_ROUTE[item.to];
    const plural = item.countLabel ?? 'items';
    const countNoun = count === 1 ? plural.replace(/s$/, '') : plural;
    return (
      <li key={item.to}>
        <NavLink
          to={item.to}
          end={item.end ?? false}
          className={({ isActive }) => cx(styles.link, nested && styles.nested, isActive && styles.active)}
          onClick={isMobile ? onMobileClose : undefined}
          title={isRail ? (hasCount ? `${item.label} (${count} ${countNoun})` : item.label) : undefined}
          // With a badge, name the link explicitly ("Unassigned, 8 tickets") so
          // the count is announced as one phrase; the visible pill is aria-hidden.
          aria-label={hasCount ? `${item.label}, ${count} ${countNoun}` : undefined}
        >
          <Icon className={styles.icon} />
          {/* The label is always in the DOM (visually hidden on the rail) so the
              link keeps its accessible name without relying on `title`. */}
          <span className={showLabels ? styles.label : 'visually-hidden'}>{item.label}</span>
          {hasCount &&
            (showLabels ? (
              <span className={styles.count} aria-hidden="true">
                {liveName ? <LiveCount name={liveName} value={count} format={formatCount} /> : formatCount(count)}
              </span>
            ) : (
              <span className={styles.dot} aria-hidden="true" />
            ))}
        </NavLink>
      </li>
    );
  }

  const brand = (
    <div className={styles.brandRow}>
      <Link to="/dashboard" className={styles.brand} onClick={isMobile ? onMobileClose : undefined} aria-label={`${brandName()} — Dashboard`}>
        <BrandMark className={styles.brandMark} />
        {showLabels && <span className={styles.brandName}>{brandName()}</span>}
      </Link>
      {isMobile && (
        <button type="button" className={styles.iconOnly} onClick={onMobileClose} aria-label="Close navigation">
          <CloseIcon />
        </button>
      )}
    </div>
  );

  const navList = (
    <nav aria-label="Primary" className={styles.nav}>
      <ul className={styles.list}>{mainItems.map((item) => renderItem(item))}</ul>
      {adminItems.length > 0 && (
        <div className={styles.group}>
          {showLabels ? (
            <button
              type="button"
              className={cx(styles.link, styles.groupToggle, onAdminRoute && styles.groupActive)}
              aria-expanded={adminOpen}
              aria-controls={adminGroupId}
              onClick={() => setAdminOpen(!adminOpen)}
            >
              <AdminGroupIcon className={styles.icon} />
              <span className={styles.label}>Admin</span>
              <ChevronDownIcon className={cx(styles.chevron, adminOpen && styles.chevronOpen)} />
            </button>
          ) : (
            <div className={styles.railDivider} role="separator" aria-label="Admin" />
          )}
          {(adminOpen || !showLabels) && (
            <ul id={adminGroupId} className={styles.list} aria-label="Admin">
              {adminItems.map((item) => renderItem(item, showLabels))}
            </ul>
          )}
        </div>
      )}
    </nav>
  );

  /* Product footer: the same G mark and product name as the header, with the
     real readiness summary (D-11) under it. The status line says "Checking
     status…" rather than claim health it hasn't verified. No hosting-platform
     branding is shown here. */
  const footer = (
    <div className={styles.footer} title={isRail ? `${brandName()}: ${status.text}` : undefined}>
      <div className={styles.hosting}>
        <BrandMark className={styles.footMark} />
        <span className={showLabels ? styles.hostingLabel : 'visually-hidden'}>{brandName()}</span>
      </div>
      <div className={styles.status} data-tone={status.tone} data-testid="sidebar-health">
        <span className={styles.statusDot} aria-hidden="true" />
        <span className={showLabels ? styles.statusText : 'visually-hidden'}>{status.text}</span>
      </div>
    </div>
  );

  if (isMobile) {
    if (!mobileOpen) return null;
    return (
      <div className={styles.backdrop} onMouseDown={onMobileClose}>
        <div
          ref={drawerRef}
          className={styles.drawer}
          role="dialog"
          aria-modal="true"
          aria-label="Primary navigation"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {brand}
          {navList}
          {footer}
        </div>
      </div>
    );
  }

  return (
    <aside id={SIDEBAR_ID} className={cx(styles.sidebar, collapsed && styles.collapsed)}>
      {brand}
      {navList}
      {footer}
    </aside>
  );
}
